import { Header, Text } from '@gasbuddy/react-components';
import { useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import React from 'react';
import useAnalyticsContext from '../../../lib/hooks/useAnalyticsContext';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { MagicLinkStatus } from '../../reducers/magiclinks';
import SupportLink from '../SupportLink';

const isLoginLink = linkType => linkType === 'login';

const logAnalyticsEvent = (analyticsContext, linkType, eventName) => {
  if (isLoginLink(linkType)) {
    analyticsContext.tagEvent({ name: eventName });
  }
};

export default function MagicLinkSent({ variant, identifierIsUsername, deviceLabel, linkType }) {
  const { t } = useTranslation('auth');
  const analytics = useAnalyticsContext();
  const emailSentString = identifierIsUsername
    ? t('magicLinkSent.emailTargetUsername')
    : t('magicLinkSent.emailTargetDirect');

  const actionText = isLoginLink(linkType) ? t('magicLinkSent.actionLogin') : t('magicLinkSent.actionAuthorize');

  switch (variant) {
    case MagicLinkStatus.Sent: {
      return (
        <React.Fragment>
          <Header as="h3">{t('magicLinkSent.sent.title')}</Header>
          <Text as="p">
            <Trans
              i18nKey="magicLinkSent.sent.message"
              ns="auth"
              values={{ linkType, emailSentString }}
            >
              We&apos;ll send a {{ linkType }} link if an account exists with {{ emailSentString }}. <br />
              Your {{ linkType }} link will expire within the next <Text bold>30 minutes</Text>.
            </Trans>
          </Text>
          <Text as="p" bold>
            <Trans
              i18nKey="magicLinkSent.sent.support"
              ns="auth"
              values={{ linkType }}
            >
              If you don&apos;t receive your {{ linkType }} link, <SupportLink>contact support</SupportLink>.
            </Trans>
          </Text>
        </React.Fragment>
      );
    }

    // TODO: There is nothing that causes this status to be set
    case MagicLinkStatus.Throttled: {
      return (
        <React.Fragment>
          <Header as="h3">{t('magicLinkSent.throttled.title')}</Header>
          <Text as="p">
            <Trans
              i18nKey="magicLinkSent.throttled.message"
              ns="auth"
              values={{ linkType, emailSentString }}
            >
              We&apos;ve recently sent a {{ linkType }} link to {{ emailSentString }}.
              You can try again in <strong>5 minutes.</strong>
            </Trans>
          </Text>
          <Text as="p" bold>
            <Trans
              i18nKey="magicLinkSent.throttled.support"
              ns="auth"
              values={{ linkType }}
            >
              If you don&apos;t receive your {{ linkType }} link, <SupportLink>contact support</SupportLink>.
            </Trans>
          </Text>
        </React.Fragment>
      );
    }

    case MagicLinkStatus.InvalidDevice: {
      logAnalyticsEvent(analytics, linkType, ANALYTICS_EVENTS.IAM_Login_MagicLink_WrongDevice);
      return (
        <React.Fragment>
          <Header as="h3">{t('magicLinkSent.invalidDevice.title', { actionText })}</Header>
          <Text as="p">
            {t('magicLinkSent.invalidDevice.message', { linkType })} <br />
          </Text>
          <Text as="p" bold>{deviceLabel}</Text>
          <Text as="p">
            {t('magicLinkSent.invalidDevice.alternative', { linkType, actionText })}
          </Text>
        </React.Fragment>
      );
    }

    case MagicLinkStatus.InvalidCode: {
      logAnalyticsEvent(analytics, linkType, ANALYTICS_EVENTS.IAM_Login_MagicLink_InvalidOrExpired);
      return (
        <React.Fragment>
          <Header as="h3">{t('magicLinkSent.invalidCode.title', { actionText })}</Header>
          <Text as="p">
            {t('magicLinkSent.invalidCode.message')}
          </Text>
          <Text as="p">
            <Trans i18nKey="magicLinkSent.invalidCode.instruction" ns="auth">
              To log in, request another link and be sure to use it within <Text bold>30 minutes</Text> of receiving the email.
            </Trans>
          </Text>
        </React.Fragment>
      );
    }

    default: {
      return false;
    }
  }
}

MagicLinkSent.propTypes = {
  variant: PropTypes.number,
  identifierIsUsername: PropTypes.bool,
  deviceLabel: PropTypes.string,
  linkType: PropTypes.oneOf(['login', 'secure']),
};

MagicLinkSent.defaultProps = {
  variant: undefined,
  identifierIsUsername: false,
  deviceLabel: '',
  linkType: 'login',
};
